import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorage } from 'angular-web-storage';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import {
  getRedemptionCodeSetProductTypeDisplay,
  getRedemptionCodeSetStatusDisplay,
  loadRedemptionCodeSetFiltersFromGetParams,
  RedemptionCodeSet,
  RedemptionCodeSetFilters,
  RedemptionCodeSetStatus
} from '../../../../../models/redemption-code';
import { ListResponse } from '../../../../../models/list-response';
import { RedemptionCodeService } from '../../../../../services/redemption-code/redemption-code.service';
import { addFiltersToGetParams, CallbackResult, CallbackResultStatus, dateStringFromISO8601 } from '../../../../../utils';
import { ChallengeStatus, getChallengeStatusDisplay } from '../../../../../models/challenge';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  CreateRedemptionCodeSetModalComponent
} from './modals/create-redemption-code-set-modal/create-redemption-code-set-modal.component';


@Component({
  selector: 'app-redemption-codes',
  templateUrl: './redemption-codes.component.html',
  styleUrls: ['./redemption-codes.component.css']
})
export class RedemptionCodesComponent implements OnInit {
  public pageNumber = 1;
  @LocalStorage() public pageSizeRedemptionCodes;
  public filters: RedemptionCodeSetFilters = {
    status: null
  };

  public filtersChangedSubject = new Subject<null>();

  public resultList: ListResponse<RedemptionCodeSet>;

  public loadingRedemptionCodeSets = true;

  public serverError: number = null;

  // The most recent result returned from a modal.
  public modalResult: CallbackResult = null;
  public lastCreatedSet: RedemptionCodeSet = null;

  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private listRequestCount = 0;

  public readonly getRedemptionCodeSetProductTypeDisplay = getRedemptionCodeSetProductTypeDisplay;
  public readonly getRedemptionCodeSetStatusDisplay = getRedemptionCodeSetStatusDisplay;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly RedemptionCodeSetStatus = RedemptionCodeSetStatus;
  public readonly CallbackResultStatus = CallbackResultStatus;
  public readonly Object = Object;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private redemptionCodeService: RedemptionCodeService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    if (!this.pageSizeRedemptionCodes) {
      this.pageSizeRedemptionCodes = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeRedemptionCodes = this.pageSizeRedemptionCodes;

    this.filtersChangedSubject.pipe(
      map(() => { this.loadingRedemptionCodeSets = true; }),
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadRedemptionCodeSets();
      addFiltersToGetParams(
        this.filters,
        this.router,
        this.activatedRoute
      );
    });

    this.filters = loadRedemptionCodeSetFiltersFromGetParams(
      this.activatedRoute
    );
    if (!this.filters.status) { this.filters.status = null; }

    this.loadRedemptionCodeSets();
  }

  public openCreateRedemptionCodeSetModal() {
    this.modalResult = null;
    this.lastCreatedSet = null;

    const modalRef = this.modalService.open(
      CreateRedemptionCodeSetModalComponent
    );
    modalRef.componentInstance.savedCallback = (createdSet: RedemptionCodeSet) => {
      this.filtersChangedSubject.next();
      this.lastCreatedSet = createdSet;
    };
  }

  /**
   * Whether a non-zero number of results have been loaded.
   */
  public resultsPresent(): boolean {
    return (
      this.resultList &&
      this.resultList.results &&
      (this.resultList.results.length > 0)
    );
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.filtersChangedSubject.next();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.resultList) { return 1; }
    return Math.ceil(this.resultList.count / this.pageSizeRedemptionCodes);
  }

  private loadRedemptionCodeSets() {
    this.loadingRedemptionCodeSets = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.redemptionCodeService
      .getRedemptionCodeSets(
        this.pageNumber,
        this.pageSizeRedemptionCodes,
        this.filters
      )
      .then(response => {
        if (thisRequestCount === this.listRequestCount) {
          this.resultList = response;
        }
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingRedemptionCodeSets = false;
        }
      });
  }

}
